import { useCallback } from 'react';
import {
  Settings,
  ShipInfo,
  TargetInfoType,
  TargetTypeEnum,
} from '@ais-ui/websockets';
import { useSettings } from '../Settings/SettingsProvider';

export enum PositionType {
  Terrestrial = 'terrestrial',
  Satellite = 'satellite',
}

export enum AisReportStatus {
  Fresh = 'fresh',
  Warning = 'warning',
  Removed = 'removed',
}

export interface AisReportAgeResult {
  aisReportAge: number;
  isAisReportOld: boolean;
  status: AisReportStatus;
}

interface AisReportAgeHook {
  getAisReportAge: (
    targetInfo: TargetInfoType,
    targetType: TargetTypeEnum
  ) => AisReportAgeResult;
  checkPositionAge: (
    positionTimestamp: Date | undefined | null,
    positionType: PositionType
  ) => AisReportAgeResult | null;
}

function checkAisReportAge(
  lastAisReport: Date | undefined | null,
  warningTimeoutInMinutes: number | undefined,
  removedTimeoutInMinutes: number | undefined
) {
  // Return null values if required data is missing
  if (!lastAisReport || !warningTimeoutInMinutes || !removedTimeoutInMinutes) {
    return {
      isAisReportOld: null,
      timeSinceLastAisReport: null,
      status: null,
    };
  }

  try {
    const currentTime = new Date();

    // Calculate the difference in milliseconds
    const timeDifferenceMs = currentTime.getTime() - lastAisReport.getTime();

    // Convert to seconds
    const timeSinceLastAisReport = timeDifferenceMs / 1000;

    // Convert to minutes for comparison
    const timeSinceLastAisReportMinutes = timeSinceLastAisReport / 60;

    // Determine status
    let status: AisReportStatus;
    if (timeSinceLastAisReportMinutes >= removedTimeoutInMinutes) {
      status = AisReportStatus.Removed;
    } else if (timeSinceLastAisReportMinutes >= warningTimeoutInMinutes) {
      status = AisReportStatus.Warning;
    } else {
      status = AisReportStatus.Fresh;
    }

    // isAisReportOld is true if warning or removed
    const isAisReportOld = status !== AisReportStatus.Fresh;

    return {
      isAisReportOld,
      timeSinceLastAisReport,
      status,
    };
  } catch (error) {
    console.error('Error checking AIS report age:', error);
    return {
      isAisReportOld: null,
      timeSinceLastAisReport: null,
      status: null,
    };
  }
}

export const useAisReportAge = (): AisReportAgeHook => {
  const { settings } = useSettings();

  // This returns the status of the NEWEST position (for "Last Position" display)
  const getAisReportAge = useCallback(
    (
      targetInfo: TargetInfoType,
      targetType: TargetTypeEnum
    ): AisReportAgeResult => {
      // Return default values for non-ship targets or missing data
      if (!targetInfo || !targetType || targetType !== TargetTypeEnum.Ship) {
        return {
          aisReportAge: 0,
          isAisReportOld: false,
          status: AisReportStatus.Fresh,
        };
      }

      const terrestrialTimeouts =
        settings['ribbonPlugin.settings']?.['target_timeouts']?.[0];
      const satelliteTimeouts =
        settings['ribbonPlugin.settings']?.['target_timeouts']?.[1];

      const lastAisReport = (targetInfo as ShipInfo)?.lastAisReport;
      const lastAisSatReport = (targetInfo as ShipInfo)?.lastAisSatReport;

      if (!lastAisReport && !lastAisSatReport) {
        return {
          aisReportAge: 0,
          isAisReportOld: false,
          status: AisReportStatus.Fresh,
        };
      }

      // Determine which is newest
      const aisTime = lastAisReport ? lastAisReport.getTime() : 0;
      const satTime = lastAisSatReport ? lastAisSatReport.getTime() : 0;

      const isAisNewer = aisTime >= satTime;
      const newestReport = isAisNewer ? lastAisReport : lastAisSatReport;
      const timeouts = isAisNewer ? terrestrialTimeouts : satelliteTimeouts;

      const { isAisReportOld, timeSinceLastAisReport, status } =
        checkAisReportAge(
          newestReport,
          timeouts?.['lost'],
          timeouts?.['deleted']
        );

      if (status !== null && timeSinceLastAisReport !== null) {
        return {
          aisReportAge: timeSinceLastAisReport,
          isAisReportOld: isAisReportOld || false,
          status: status,
        };
      }

      // Fallback to Fresh if no valid data
      return {
        aisReportAge: 0,
        isAisReportOld: false,
        status: AisReportStatus.Fresh,
      };
    },
    [settings]
  );



  const checkPositionAge = useCallback(
    (
      positionTimestamp: Date | undefined | null,
      positionType: PositionType
    ): AisReportAgeResult | null => {
      if (!positionTimestamp) {
        return null;
      }

      const timeoutSettings =
        settings['ribbonPlugin.settings']?.['target_timeouts'];

      // Index 0 is terrestrial, index 1 is satellite
      const timeoutIndex = positionType === PositionType.Satellite ? 1 : 0;
      const warningTimeout = timeoutSettings?.[timeoutIndex]?.['lost'];
      const removedTimeout = timeoutSettings?.[timeoutIndex]?.['deleted'];

      try {
        const { isAisReportOld, timeSinceLastAisReport, status } =
          checkAisReportAge(positionTimestamp, warningTimeout, removedTimeout);

        if (
          isAisReportOld === null ||
          timeSinceLastAisReport === null ||
          status === null
        ) {
          return null;
        }

        return {
          aisReportAge: timeSinceLastAisReport,
          isAisReportOld,
          status,
        };
      } catch (error) {
        console.error('Error checking position age:', error);
        return null;
      }
    },
    [settings]
  );

  return {
    getAisReportAge,
    checkPositionAge,
  };
};
